import { useTranslation } from '@crew/modules/i18n'
import {
  ComponentCallbackHandler,
  convertSecondsToHHMMSS,
  convertSecondsToMMSS,
} from '@crew/utils'
import { CrewUserAvatar } from 'components/elements/crewUserAvatar/crewUserAvatar'
import { memo, useCallback, useMemo, useState } from 'react'
import AccountCircle from '~icons/material-symbols/account-circle'
import MaterialSymbolsAdd from '~icons/material-symbols/add'
import BaselineDelete from '~icons/ic/baseline-delete'
import MdiPencil from '~icons/mdi/pencil'
import { EventRecordingTranscribe } from '@crew/apis/eventRecordingTranscribe/models/getEventRecordingTranscribes/response'
import { EventRecordingTranscribeItemInputForm } from './components/eventRecordingTranscribeItemInputForm/eventRecordingTranscribeItemInputForm'
import { useEventRecordingTranscribeItem } from './useEventRecordingTranscribeItem'
import { useToast } from 'hooks/useToast'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useModal } from 'components/layouts/modal/useModal'
import { CrewConfirmDialog } from 'components/elements/crewConfirmDialog/crewConfirmDialog'
import { useValueChangeEffect } from '@crew/hooks'
import {
  AudioState,
  EventRecordingTranscribeAudioPlayButton,
} from './components/eventRecordingTranscribeAudioPlayButton/eventRecordingTranscribeAudioPlayButton'
import { useInView } from 'react-intersection-observer'
import { setScrollToTranscribeId } from 'features/event/components/eventDetailPage/states/eventDetailPageSlice'
import { useAppDispatch } from 'states/hooks'
import { useEventDetailPageContext } from 'features/event/components/eventDetailPage/useEventDetailPage'
import { CrewCheckBox } from 'components/devextreme/crewCheckBox'
import { useEventDetailMeetingMinutesContext } from 'features/event/components/eventDetailPage/components/eventDetailMeetingMinutes/components/eventDetailMeetingMinutes'

export type EventRecordingTranscribeItemProps = {
  // この文字起こしが属するイベント録画中での発言時刻の最大値
  maxSpeechTime: number | undefined
  // 編集状態の管理
  editingTranscribeId: string | null
  setEditingTranscribeId: (id: string | null) => void
  // イベント録画文字起こし
  eventRecordingTranscribe: EventRecordingTranscribe
  // 次の文字起こしの開始時刻
  nextSpeechTimestamp: number | null
  // 音声再生制御
  audioState: AudioState
  audioCurrentRequester: string | null
  audioPlay?: (requester: string, start: number, end: number | null) => void
  audioStop?: () => void
}

export const EventRecordingTranscribeItem = memo(
  (props: EventRecordingTranscribeItemProps) => {
    const { deleteEventRecordingTranscribe, isLoadingDeleteTranscribe } =
      useEventRecordingTranscribeItem()

    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const toast = useToast()
    const [generalShowApiErrors] = useShowApiErrors()

    const [isAddingTranscribe, setIsAddingTranscribe] = useState<boolean>(false)

    const { setIsEditTranscribe } = useEventDetailMeetingMinutesContext()

    // 文字起こしの選択済みリストを設定する関数を取得
    const {
      selectedEventRecordingTranscribes,
      setSelectedEventRecordingTranscribes,
    } = useEventDetailPageContext()

    // 削除確認ダイアログ
    const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
      useModal()
    // 確認ダイアログメッセージ
    const [confirmMessage, setConfirmMessage] = useState('')

    // 発言時刻のrender
    const renderSpeechTime = useCallback(
      (second: number) => {
        const hour = 60 * 60

        // 発言時刻の最大時間が取得できない場合は何も描画しない
        if (!props.maxSpeechTime) {
          return null
        }

        // 小数点以下は表示しないため切り捨てる
        const roundedSecond = Math.floor(second)

        // 発言時間の最大値によって表示形式を決める
        //   →1時間以上の発言がある　場合：[h:mm:ss]形式で表示
        //   →1時間未満の発言しかない場合：[m:ss]形式で表示
        let speechTime
        if (props.maxSpeechTime > hour) {
          // 1時間以上の発言がある場合は、[h:mm:ss]形式で表示
          speechTime = convertSecondsToHHMMSS(roundedSecond)
        } else {
          // 1時間未満の発言しかない場合は、[m:ss]形式で表示
          speechTime = convertSecondsToMMSS(roundedSecond)
        }

        return speechTime
      },
      [props.maxSpeechTime]
    )

    // 編集ボタンクリック時
    const handleEditButtonClick = useCallback(() => {
      // 編集状態を「編集中」に更新
      setIsEditTranscribe(true)

      setIsAddingTranscribe(false)
      props.setEditingTranscribeId(props.eventRecordingTranscribe.id)
    }, [props, setIsEditTranscribe])

    // 追加ボタンクリック時
    const handleAddButtonClick = useCallback(() => {
      // 編集状態を「編集中」に更新
      setIsEditTranscribe(true)

      setIsAddingTranscribe(true)
      props.setEditingTranscribeId(props.eventRecordingTranscribe.id)
    }, [props, setIsEditTranscribe])

    // 削除ボタンクリック時
    const handleDeleteButtonClick = useCallback(() => {
      // 確認ダイアログの表示（処理は確認ダイアログのOKボタン押下時に行う）
      setConfirmMessage(t('message.general.confirmMessage.delete'))
      openConfirmDialog()
    }, [openConfirmDialog, t])

    // 削除確認ダイアログの許可ボタンクリック時
    const handleDeletePermitButtonClick = useCallback(async () => {
      try {
        // 文字起こし削除APIを実行
        await deleteEventRecordingTranscribe(
          props.eventRecordingTranscribe.id,
          props.eventRecordingTranscribe.version
        )
        toast.success(
          t('message.eventRecordingTranscribe.eventRecordingTranscribeDeleted')
        )

        // 削除した行が選択状態だった可能性があるため
        // リストに含まれている場合は、選択済みリストから削除する
        setSelectedEventRecordingTranscribes((prev) =>
          prev.filter(
            (transcribe) => transcribe.id !== props.eventRecordingTranscribe.id
          )
        )
      } catch (err) {
        generalShowApiErrors(err)
      }
      // 確認ダイアログを閉じる
      closeConfirmDialog()
    }, [
      closeConfirmDialog,
      deleteEventRecordingTranscribe,
      generalShowApiErrors,
      props.eventRecordingTranscribe.id,
      props.eventRecordingTranscribe.version,
      setSelectedEventRecordingTranscribes,
      t,
      toast,
    ])

    // 編集状態を終了する
    const handleCloseInputForm = useCallback(() => {
      // 編集状態を「未編集」に更新
      setIsEditTranscribe(false)

      setIsAddingTranscribe(false)
      props.setEditingTranscribeId(null)
    }, [props, setIsEditTranscribe])

    // 編集状態の文字起こしIdが変更された場合
    useValueChangeEffect(
      () => {
        // 自分以外が編集状態になった場合は追加モードの状態を終了させる
        if (props.editingTranscribeId !== props.eventRecordingTranscribe.id) {
          setIsAddingTranscribe(false)
        }
      },
      [props],
      props.editingTranscribeId
    )

    // 行修正状態の判定
    const isEditingTranscribe = useMemo(() => {
      return (
        props.editingTranscribeId === props.eventRecordingTranscribe.id &&
        !isAddingTranscribe
      )
    }, [
      isAddingTranscribe,
      props.editingTranscribeId,
      props.eventRecordingTranscribe.id,
    ])

    // 手動で追加した文字起こしか
    const isManualTranscribe = useMemo(() => {
      // 発言時刻がnull＝手動で追加した文字起こし
      return props.eventRecordingTranscribe.speechTimestamp == null
    }, [props.eventRecordingTranscribe.speechTimestamp])

    // メッセージの要素が一部でも表示されたらメッセージの表示フラグを更新する
    const { ref: transcribeRef } = useInView({
      threshold: 1.0, // すべて表示したときに発火する
      onChange: (inView) => {
        // 文字起こしを描画時、スクロール位置を保存する
        if (inView) {
          dispatch(setScrollToTranscribeId(props.eventRecordingTranscribe.id))
        }
      },
    })

    // チェックボックスの値が変更された場合の処理
    const handleTranscribeItemCheckboxValueChange = useCallback<
      ComponentCallbackHandler<typeof CrewCheckBox, 'onValueChange'>
    >(
      (checked) => {
        // チェックボックスの値が変更された場合、選択済みリストを更新する
        if (checked) {
          // チェックされた場合は選択済みリストに追加
          setSelectedEventRecordingTranscribes((prev) => [
            ...prev,
            {
              id: props.eventRecordingTranscribe.id,
              version: props.eventRecordingTranscribe.version,
            },
          ])
        } else {
          // チェックが外れた場合は選択済みリストから削除
          setSelectedEventRecordingTranscribes((prev) =>
            prev.filter(
              (transcribe) =>
                transcribe.id !== props.eventRecordingTranscribe.id
            )
          )
        }
      },
      [
        props.eventRecordingTranscribe.id,
        props.eventRecordingTranscribe.version,
        setSelectedEventRecordingTranscribes,
      ]
    )

    // 対象のアイテムがチェック状態かを保持する変数（編集状態と表示状態で同期させるために使用する）
    const isTranscribeItemChecked = useMemo(() => {
      // 選択済みIDリストに現在の文字起こしIDが含まれているかどうかを判定
      return (
        selectedEventRecordingTranscribes.findIndex(
          (item) => item.id === props.eventRecordingTranscribe.id
        ) !== -1
      )
    }, [props.eventRecordingTranscribe.id, selectedEventRecordingTranscribes])

    return (
      <div id={props.eventRecordingTranscribe.id}>
        {isEditingTranscribe ? (
          <div className="flex flex-row">
            {/* 一括処理用チェックボックス */}
            <div className="flex items-center">
              <CrewCheckBox
                value={isTranscribeItemChecked}
                onValueChange={handleTranscribeItemCheckboxValueChange}
              />
            </div>
            {/* 行修正状態の場合 */}
            <div className="flex-1">
              <EventRecordingTranscribeItemInputForm
                isEditMode={true}
                maxSpeechTime={props.maxSpeechTime}
                eventRecordingTranscribe={props.eventRecordingTranscribe}
                onClose={handleCloseInputForm}
                nextSpeechTimestamp={props.nextSpeechTimestamp}
                audioState={props.audioState}
                audioCurrentRequester={props.audioCurrentRequester}
                audioPlay={props.audioPlay}
                audioStop={props.audioStop}
              />
            </div>
          </div>
        ) : (
          <div ref={transcribeRef}>
            {/* 表示状態の場合 */}
            <div className="flex flex-row gap-x-1 hover:bg-crew-gray-1-light hover:dark:bg-crew-gray-4-dark">
              {/* 一括処理用チェックボックス */}
              <div className="flex items-center">
                <CrewCheckBox
                  value={isTranscribeItemChecked}
                  onValueChange={handleTranscribeItemCheckboxValueChange}
                />
              </div>

              {/* アバター */}
              <div>
                {props.eventRecordingTranscribe.speaker ? (
                  <div
                    className="m-[4px]" // AccountCircleのsvgに余白が発生する関係で表示位置がずれるため、マージンを入れて調整
                  >
                    {/* 発言者IDが登録されている場合：ユーザーのアバターを表示 */}
                    <CrewUserAvatar
                      userId={props.eventRecordingTranscribe.speaker.id}
                      displayName={
                        props.eventRecordingTranscribe.speaker.displayName
                      }
                      cacheValue={
                        props.eventRecordingTranscribe.speaker.id +
                        props.eventRecordingTranscribe.speaker.version
                      }
                    />
                  </div>
                ) : (
                  // 発言者が登録されていない or 発言者名のみ登録されている場合：デフォルトのアバターを表示
                  <AccountCircle
                    width={48}
                    height={48}
                    className={'crew-text-gray-5'}
                  />
                )}
              </div>
              <div className="flex flex-col justify-center gap-y-0.5">
                <div className="flex flex-row gap-x-2 text-sm">
                  {/* 発言者 */}
                  <div className="crew-text-gray-4 font-bold">
                    {props.eventRecordingTranscribe.speaker?.displayName ??
                      props.eventRecordingTranscribe.speakerName ??
                      t('label.unknown')}
                  </div>

                  {/* 発言時間 */}
                  <div className="crew-text-gray-5">
                    {props.eventRecordingTranscribe.speechTimestamp != null &&
                      props.eventRecordingTranscribe.speechTimestamp >= 0 &&
                      renderSpeechTime(
                        props.eventRecordingTranscribe.speechTimestamp
                      )}
                  </div>
                </div>

                {/* 発言内容 */}
                <div>{props.eventRecordingTranscribe.speechContent}</div>
              </div>

              <div className="flex flex-row gap-x-2 ml-auto items-center">
                {/* 再生ボタン（手動で追加した文字起こしには表示しない） */}
                {!isManualTranscribe && (
                  <EventRecordingTranscribeAudioPlayButton
                    eventRecordingTranscribeId={
                      props.eventRecordingTranscribe.id
                    }
                    speechTimestamp={
                      props.eventRecordingTranscribe.speechTimestamp ?? 0
                    }
                    nextSpeechTimestamp={props.nextSpeechTimestamp}
                    audioState={props.audioState}
                    audioCurrentRequester={props.audioCurrentRequester}
                    audioPlay={props.audioPlay}
                    audioStop={props.audioStop}
                  />
                )}

                {/* 編集ボタン */}
                <div
                  className="cursor-pointer hover:opacity-70"
                  onClick={handleEditButtonClick}
                >
                  <MdiPencil width={24} height={24} />
                </div>

                {/* 追加ボタン */}
                <div
                  className="cursor-pointer hover:opacity-70"
                  onClick={handleAddButtonClick}
                >
                  <MaterialSymbolsAdd width={24} height={24} />
                </div>

                {/* 削除ボタン */}
                <div
                  className="cursor-pointer hover:opacity-70"
                  onClick={handleDeleteButtonClick}
                >
                  <BaselineDelete width={24} height={24} />
                </div>
              </div>
            </div>

            {/* 行追加状態の場合 */}
            {isAddingTranscribe && (
              <EventRecordingTranscribeItemInputForm
                isEditMode={false}
                maxSpeechTime={props.maxSpeechTime}
                eventRecordingTranscribe={props.eventRecordingTranscribe}
                onClose={handleCloseInputForm}
                nextSpeechTimestamp={props.nextSpeechTimestamp}
                audioState={props.audioState}
                audioCurrentRequester={props.audioCurrentRequester}
                audioPlay={props.audioPlay}
                audioStop={props.audioStop}
              />
            )}
          </div>
        )}

        {/* 削除確認ダイアログ */}
        <CrewConfirmDialog
          isOpen={isConfirmDialogOpen}
          message={confirmMessage}
          onPermitButtonClick={handleDeletePermitButtonClick}
          onCancelButtonClick={closeConfirmDialog}
          permitButtonDisabled={isLoadingDeleteTranscribe}
        />
      </div>
    )
  }
)
